import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'mr' | 'hi' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Record<string, Record<Language, string>>;
}

const defaultTranslations = {
  // Navigation and Common
  home: {
    mr: 'मुख्यपृष्ठ',
    hi: 'मुख्य पृष्ठ',
    en: 'Home',
  },
  aboutUs: {
    mr: 'आमच्याबद्दल',
    hi: 'हमारे बारे में',
    en: 'About Us',
  },
  departments: {
    mr: 'विभाग',
    hi: 'विभाग',
    en: 'Departments',
  },
  services: {
    mr: 'सेवा',
    hi: 'सेवाएं',
    en: 'Services',
  },
  schemes: {
    mr: 'योजना',
    hi: 'योजनाएं',
    en: 'Schemes',
  },
  gallery: {
    mr: 'गॅलरी',
    hi: 'गैलरी',
    en: 'Gallery',
  },
  contact: {
    mr: 'संपर्क',
    hi: 'संपर्क',
    en: 'Contact',
  },
  adminLogin: {
    mr: 'प्रशासक लॉगिन',
    hi: 'व्यवस्थापक लॉगिन',
    en: 'Admin Login',
  },

  // Header
  welcome: {
    mr: 'मान ग्रामपंचायत मध्ये आपले स्वागत आहे',
    hi: 'मान ग्राम पंचायत में आपका स्वागत है',
    en: 'Welcome to Maan Grampanchayat',
  },
  emergency: {
    mr: 'आपत्कालीन',
    hi: 'आपातकालीन',
    en: 'Emergency',
  },

  // Home Page
  latestUpdates: {
    mr: 'ताज्या अपडेट्स',
    hi: 'नवीनतम अपडेट',
    en: 'Latest Updates',
  },
  viewAll: {
    mr: 'सर्व पहा',
    hi: 'सभी देखें',
    en: 'View All',
  },
  quickLinks: {
    mr: 'जलद दुवे',
    hi: 'त्वरित लिंक',
    en: 'Quick Links',
  },
  upcomingEvents: {
    mr: 'येणारे कार्यक्रम',
    hi: 'आगामी कार्यक्रम',
    en: 'Upcoming Events',
  },
  readMore: {
    mr: 'अधिक वाचा',
    hi: 'और पढ़ें',
    en: 'Read More',
  },

  // Schemes Page
  availableSchemes: {
    mr: 'उपलब्ध योजना',
    hi: 'उपलब्ध योजनाएं',
    en: 'Available Schemes',
  },
  applyNow: {
    mr: 'अर्ज करा',
    hi: 'आवेदन करें',
    en: 'Apply Now',
  },
  keyBenefits: {
    mr: 'प्रमुख फायदे',
    hi: 'प्रमुख लाभ',
    en: 'Key Benefits',
  },

  // Gallery Page
  photoGallery: {
    mr: 'छायाचित्र गॅलरी',
    hi: 'फोटो गैलरी',
    en: 'Photo Gallery',
  },
  villageLife: {
    mr: 'गाव जीवन',
    hi: 'गांव का जीवन',
    en: 'Village Life',
  },
  culturalEvents: {
    mr: 'सांस्कृतिक कार्यक्रम',
    hi: 'सांस्कृतिक कार्यक्रम',
    en: 'Cultural Events',
  },
  development: {
    mr: 'विकास कामे',
    hi: 'विकास कार्य',
    en: 'Development Works',
  },

  // Contact Page
  contactUs: {
    mr: 'आमच्याशी संपर्क साधा',
    hi: 'हमसे संपर्क करें',
    en: 'Contact Us',
  },
  address: {
    mr: 'पत्ता',
    hi: 'पता',
    en: 'Address',
  },
  phoneNumber: {
    mr: 'दूरध्वनी क्रमांक',
    hi: 'फोन नंबर',
    en: 'Phone Number',
  },
  emailAddress: {
    mr: 'ईमेल पत्ता',
    hi: 'ईमेल पता',
    en: 'Email Address',
  },
  sendMessage: {
    mr: 'संदेश पाठवा',
    hi: 'संदेश भेजें',
    en: 'Send Message',
  },

  // Footer
  usefulLinks: {
    mr: 'उपयुक्त दुवे',
    hi: 'उपयोगी लिंक',
    en: 'Useful Links',
  },
  followUs: {
    mr: 'आम्हाला फॉलो करा',
    hi: 'हमें फॉलो करें',
    en: 'Follow Us',
  },
  copyright: {
    mr: 'सर्व हक्क राखीव',
    hi: 'सर्वाधिकार सुरक्षित',
    en: 'All Rights Reserved',
  },

  // Admin Panel
  login: {
    mr: 'लॉग इन',
    hi: 'लॉग इन',
    en: 'Login',
  },
  username: {
    mr: 'वापरकर्तानाव',
    hi: 'उपयोगकर्ता नाम',
    en: 'Username',
  },
  password: {
    mr: 'पासवर्ड',
    hi: 'पासवर्ड',
    en: 'Password',
  },
  dashboard: {
    mr: 'डॅशबोर्ड',
    hi: 'डैशबोर्ड',
    en: 'Dashboard',
  },
  edit: {
    mr: 'संपादित करा',
    hi: 'संपादित करें',
    en: 'Edit',
  },
  save: {
    mr: 'जतन करा',
    hi: 'सहेजें',
    en: 'Save',
  },
  cancel: {
    mr: 'रद्द करा',
    hi: 'रद्द करें',
    en: 'Cancel',
  },

  // Common Actions
  submit: {
    mr: 'सबमिट करा',
    hi: 'सबमिट करें',
    en: 'Submit',
  },
  search: {
    mr: 'शोधा',
    hi: 'खोजें',
    en: 'Search',
  },
  download: {
    mr: 'डाउनलोड करा',
    hi: 'डाउनलोड करें',
    en: 'Download',
  },
  loading: {
    mr: 'लोड होत आहे...',
    hi: 'लोड हो रहा है...',
    en: 'Loading...',
  },
  noResults: {
    mr: 'कोणतेही परिणाम नाहीत',
    hi: 'कोई परिणाम नहीं',
    en: 'No Results Found',
  },
};

export const LanguageContext = createContext<LanguageContextType>({
  language: 'mr',
  setLanguage: () => {},
  translations: defaultTranslations,
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('mr');

  const value = {
    language,
    setLanguage,
    translations: defaultTranslations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
